import useJiraTicket from "./services/useJiraTicket";
import useJiraComments, { useAddJiraComment } from "./services/useJiraComments";
import { jiraRichTextToPlainString, jiraAdfToHtml } from "./jiraRichText";
import { filterPublicJiraComments } from "./jiraComment";
import Loader from "../Loader";
import Button from "../common/Button";
import classNames from "classnames";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import Toast from "../Toast";
import {
  faChevronDown,
  faChevronUp,
  faTicketSimple,
  faCommentsQuestionCheck,
  faHeadset,
  faPaperPlaneTop,
  faUser,
} from "@fortawesome/pro-regular-svg-icons";
import AwesomeIcon from "../AwesomeIcon";
import { useTranslation } from "react-i18next";
import { useAuth } from "@qure/qure-auth";

interface TicketListItemProps {
  ticketKey: string;
  isExpanded: boolean;
  onToggle: () => void;
  onRemove?: (ticketKey: string) => void;
  variant?: "default" | "dark";
}

const TicketListItem = ({
  ticketKey,
  isExpanded,
  onToggle,
  variant = "default",
}: TicketListItemProps) => {
  const { t } = useTranslation();
  const { userInfo }: any = useAuth();
  const currentUserName: string | undefined = userInfo?.details?.name;
  // Always fetch ticket data to show status in collapsed view
  const { data: ticket, isLoading, error } = useJiraTicket(ticketKey, true);
  // Only fetch comments when expanded
  const { data: commentsData, isLoading: isLoadingComments } = useJiraComments(
    ticketKey,
    isExpanded
  );
  const addCommentMutation = useAddJiraComment(ticketKey);
  const [commentText, setCommentText] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const sendBtnRef = useRef<HTMLButtonElement>(null);
  const [showComments, setShowComments] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(false);

  /** Modal-only: reset read-more / comments when ticket collapses */
  useEffect(() => {
    if (!isExpanded) {
      setShowComments(false);
      if (variant === "dark") {
        setSummaryExpanded(false);
      }
    }
  }, [isExpanded, variant]);

  const getStatusColor = (colorName?: string) => {
    switch (colorName?.toLowerCase()) {
      case "green":
        return "bg-green-500";
      case "yellow":
        return "bg-yellow-500";
      case "blue-gray":
      case "blue":
        return "bg-blue-500";
      default:
        return "bg-gray-500";
    }
  };

  /** Status pill: dark theme (standalone dark variant) */
  const getStatusPillStyle = (colorName?: string) => {
    switch (colorName?.toLowerCase()) {
      case "green":
        return "bg-green-800/80 text-green-200";
      case "yellow":
        return "bg-yellow-800 text-yellow-300";
      case "blue-gray":
      case "blue":
        return "bg-blue-800/80 text-blue-200";
      default:
        return "bg-gray-700 text-gray-300";
    }
  };

  /** Support modal header pill — light: Figma Attention/200+700; dark: existing modal tokens */
  const getStatusPillHeaderModal = (colorName?: string) => {
    switch (colorName?.toLowerCase()) {
      case "green":
        return "bg-green-100 text-green-800 dark:bg-green-900/80 dark:text-green-200";
      case "yellow":
        return "bg-[#ffdca2] text-[#b16f00] dark:bg-[#895700] dark:text-[#ffc86c]";
      case "blue-gray":
      case "blue":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/70 dark:text-blue-200";
      default:
        return "bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-200";
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) {
      Toast.error(t("support.please_enter_comment"));
      return;
    }

    try {
      await addCommentMutation.mutateAsync({ body: commentText });
      setCommentText("");
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
      if (sendBtnRef.current) {
        sendBtnRef.current.style.top = "12px";
        sendBtnRef.current.style.bottom = "auto";
      }
      Toast.success(t("support.comment_added_success"));
    } catch (error) {
      Toast.error(t("support.failed_to_add_comment"));
    }
  };

  const formatNewlinesAsBr = (text: string) => text.replace(/\n/g, "<br />");

  // Get status from ticket data or show loading
  const statusDisplay = ticket?.fields?.status?.name || t("support.loading");
  const statusColor = ticket?.fields?.status?.statusCategory?.colorName;
  const raisedDate =
    ticket?.fields?.created &&
    new Date(ticket.fields.created).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  const comments = filterPublicJiraComments(commentsData?.comments);
  const commentCount = comments.length;

  const ticketTitle = ticket?.fields?.summary ?? "";
  const descSource = ticket?.fields?.description
    ? jiraRichTextToPlainString(ticket.fields.description).trim()
    : "";
  const descDistinct = descSource && descSource !== ticketTitle.trim() ? descSource : "";
  const descHtml =
    descDistinct && ticket?.fields?.description
      ? jiraAdfToHtml(ticket.fields.description)
      : "";

  /** Figma: summary uses 3-line clamp; Read more only if content overflows */
  const summaryRef = useRef<HTMLDivElement>(null);
  const [summaryClampedOverflows, setSummaryClampedOverflows] = useState(false);

  useLayoutEffect(() => {
    if (variant !== "dark" || !isExpanded || !descDistinct) {
      setSummaryClampedOverflows(false);
      return;
    }
    if (summaryExpanded) {
      return;
    }
    const el = summaryRef.current;
    if (!el) {
      setSummaryClampedOverflows(false);
      return;
    }
    const measure = () => {
      setSummaryClampedOverflows(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    const id = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(id);
  }, [isExpanded, descDistinct, summaryExpanded, variant]);

  if (variant === "dark") {
    return (
      <div className="bg-[#f6f7f7] dark:bg-[#29333e] rounded-8 overflow-hidden border border-[#d9dbdd] dark:border-[#474f58]">
        {/* Light: Figma — header #BBBFC2, title #0C1723; dark: header #474F58, title #F6F7F7 */}
        <button
          type="button"
          onClick={onToggle}
          className="w-full flex items-center justify-between gap-3 px-5 py-4 bg-[#bbbfc2] hover:bg-[#a8adb1] dark:bg-[#474f58] dark:hover:bg-[#525a64] transition-colors text-left font-normal"
        >
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <AwesomeIcon
              name={faTicketSimple}
              className="shrink-0 w-6 h-6 text-[#0c1723] dark:text-gray-100"
            />
            {isLoading && !ticketTitle ? (
              <span className="inline-block h-5 w-48 rounded bg-[#a8adb1] dark:bg-[#525a64] animate-pulse" />
            ) : (
              <span className="font-sans font-medium text-lg text-[#0c1723] dark:text-gray-100 truncate min-w-0">
                {ticketTitle || ticketKey}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {!isLoading && (
              <span
                className={classNames(
                  "inline-flex items-center h-6 px-3 py-2 rounded-[38px] text-md font-medium leading-4 shrink-0",
                  getStatusPillHeaderModal(statusColor)
                )}
              >
                {statusDisplay}
              </span>
            )}
            {isLoading && <Loader size="small" />}
            <AwesomeIcon
              name={isExpanded ? faChevronUp : faChevronDown}
              className="w-4 h-4 shrink-0 text-[#0c1723] dark:text-[#f6f7f7]"
            />
          </div>
        </button>

        {isExpanded && (
          <>
            {error && (
              <div className="mx-6 mt-4 rounded-8 border border-red-200 bg-red-50 px-5 py-3 dark:border-red-500/40 dark:bg-red-950/40">
                <p className="text-md leading-4 text-red-800 dark:text-red-200">
                  {t("support.failed_to_load_ticket_info")}: {error.message}
                </p>
              </div>
            )}

            {/* Light: Body #0C1723, metadata #474F58; dark: #F6F7F7 / #BBBFC2 */}
            <div className="flex flex-col gap-2 px-6 py-5 text-left">
              {descDistinct ? (
                <div className="font-sans text-base font-normal leading-[22px] tracking-[0] text-[#0c1723] dark:text-[#f6f7f7] overflow-hidden">
                  {summaryExpanded ? (
                    <div
                      className="prose prose-sm max-w-none break-words text-[#0c1723] dark:prose-invert dark:text-[#f6f7f7] [&_a]:text-[#008280] dark:[&_a]:text-[#00d0cd] [&_a]:break-all [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0.5 [&_code]:font-mono [&_code]:bg-[#e8eaeb] [&_code]:dark:bg-[#1a2530] [&_code]:rounded [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-[inherit] [&_pre]:bg-[#e8eaeb] [&_pre]:dark:bg-[#1a2530] [&_pre]:rounded-md [&_pre]:p-3 [&_pre]:my-2 [&_pre_code]:p-0 [&_pre_code]:rounded-none [&_pre_code]:bg-transparent"
                      dangerouslySetInnerHTML={{ __html: descHtml }}
                    />
                  ) : (
                    <div
                      ref={summaryRef}
                      className="prose prose-sm max-w-none break-words line-clamp-3 text-[#0c1723] dark:prose-invert dark:text-[#f6f7f7] [&_a]:text-[#008280] dark:[&_a]:text-[#00d0cd] [&_a]:break-all [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0.5 [&_code]:font-mono [&_code]:bg-[#e8eaeb] [&_code]:dark:bg-[#1a2530] [&_code]:rounded [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-[inherit] [&_pre]:bg-[#e8eaeb] [&_pre]:dark:bg-[#1a2530] [&_pre]:rounded-md [&_pre]:p-3 [&_pre]:my-2 [&_pre_code]:p-0 [&_pre_code]:rounded-none [&_pre_code]:bg-transparent"
                      dangerouslySetInnerHTML={{ __html: descHtml }}
                    />
                  )}
                  {summaryClampedOverflows && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSummaryExpanded(!summaryExpanded);
                      }}
                      className="mt-2 font-sans text-base font-normal leading-[22px] text-[#008280] underline decoration-solid [text-decoration-skip-ink:none] hover:opacity-90 dark:text-[#00d0cd]"
                    >
                      {summaryExpanded
                        ? t("support.read_less", { defaultValue: "Read less" })
                        : t("support.read_more", { defaultValue: "Read more" })}
                    </button>
                  )}
                </div>
              ) : (
                !isLoading && (
                  <p className="font-sans text-md font-normal leading-4 text-[#474f58] dark:text-[#bbbfc2]">
                    {t("support.no_additional_details", {
                      defaultValue: "No additional details",
                    })}
                  </p>
                )
              )}

              <div className="flex justify-end w-full">
                <span className="inline-flex items-start gap-2 font-sans text-md font-normal leading-4 text-[#474f58] dark:text-[#bbbfc2] whitespace-nowrap">
                  <span>{ticketKey}</span>
                  <span>|</span>
                  <span>
                    {raisedDate
                      ? `${t("support.raised_on", {
                          defaultValue: "raised on",
                        })} ${raisedDate}`
                      : t("support.not_available")}
                  </span>
                </span>
              </div>
            </div>

            {/* Light: border #D9DBDD; link Product Teal/500 #008280 */}
            <div className="border-t border-[#d9dbdd] flex flex-col gap-2 px-6 py-4 dark:border-[#474f58]">
              {commentCount > 0 && (
                <div className="flex items-center min-h-4">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowComments(!showComments);
                    }}
                    className="font-sans text-md font-medium leading-4 text-[#008280] underline decoration-solid [text-decoration-skip-ink:none] hover:opacity-90 dark:text-[#00a9a7]"
                  >
                    {showComments
                      ? t("support.hide_comments")
                      : t("support.view_comments_count", {
                          count: commentCount,
                          defaultValue:
                            commentCount === 1
                              ? "View 1 comment"
                              : `View ${commentCount} comments`,
                        })}
                  </button>
                </div>
              )}

              {/* Thread gap-16 between rows; row gap-8 py-4 agent; bubble px-16 py-8 gap-4 */}
              <div className="flex flex-col gap-5 py-3">
                {(commentCount > 0 ? showComments : true) && (
                  <>
                    {isLoadingComments && (
                      <div className="flex justify-center py-4">
                        <Loader size="small" label={t("support.loading_comments")} />
                      </div>
                    )}

                    {!isLoadingComments && commentCount > 0 && (
                      <div className="flex flex-col gap-5 max-h-64 scrollbar pr-3">
                        {comments.map((comment) => {
                          const displayName = comment.author?.displayName ?? "";
                          const isCustomer =
                            displayName === "Customer" ||
                            (!!currentUserName && displayName === currentUserName);
                          return (
                            <div
                              key={comment.id}
                              className={classNames(
                                "flex gap-3 items-start py-2",
                                isCustomer
                                  ? "flex-row pl-8 sm:pl-[120px]"
                                  : "flex-row pr-8 sm:pr-[120px]"
                              )}
                            >
                              {!isCustomer && (
                                <div className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center bg-[#ff7869]">
                                  <AwesomeIcon
                                    name={faHeadset}
                                    className="w-5 h-5 text-[#f6f7f7]"
                                  />
                                </div>
                              )}
                              <div className="min-w-0 flex-1 max-w-[464px] rounded-8 flex flex-col gap-2 border border-[#d9dbdd] bg-transparent px-5 py-3 overflow-hidden dark:border-[#474f58]">
                                <div className="flex flex-wrap gap-2 items-center font-sans text-md leading-4 text-[#0c1723] dark:text-[#f6f7f7] w-full min-w-0 flex-row">
                                  <span className="font-medium shrink-0">
                                    {isCustomer
                                      ? t("support.you", { defaultValue: "You" })
                                      : comment.author?.displayName ??
                                        t("support.unknown")}
                                  </span>
                                  <span className="font-normal shrink-0">|</span>
                                  <span className="font-normal truncate">
                                    {comment.created
                                      ? new Date(comment.created).toLocaleDateString(
                                          "en-GB",
                                          {
                                            day: "numeric",
                                            month: "short",
                                            year: "numeric",
                                          }
                                        )
                                      : ""}
                                  </span>
                                </div>
                                <div
                                  className="font-sans text-base font-normal leading-[22px] tracking-[0] text-[#0c1723] text-left prose prose-sm max-w-none break-all dark:prose-invert dark:text-[#f6f7f7] [&_a]:text-[#008280] dark:[&_a]:text-[#00d0cd] [&_a]:break-all [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0.5 [&_code]:font-mono [&_code]:bg-[#e8eaeb] [&_code]:dark:bg-[#1a2530] [&_code]:rounded [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-[inherit] [&_pre]:bg-[#e8eaeb] [&_pre]:dark:bg-[#1a2530] [&_pre]:rounded-md [&_pre]:p-3 [&_pre]:my-2 [&_pre_code]:p-0 [&_pre_code]:rounded-none [&_pre_code]:bg-transparent"
                                  dangerouslySetInnerHTML={{
                                    __html: jiraAdfToHtml(comment.body),
                                  }}
                                />
                              </div>
                              {isCustomer && (
                                <div className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center bg-[#008280]">
                                  <AwesomeIcon
                                    name={faUser}
                                    className="w-5 h-5 text-[#f6f7f7]"
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {!isLoadingComments && commentCount === 0 && (
                      <p className="font-sans text-md leading-4 text-[#81878d] py-2">
                        {t("support.no_comments_yet")}
                      </p>
                    )}
                  </>
                )}

                {/* h-48 p-8 gap-8; Body-L placeholder 16/24 #474F58; icons 24px */}
                {!isLoadingComments && (commentCount === 0 || showComments) && (
                  <div className="flex gap-3 items-center w-full">
                    <div className="flex-1 relative min-h-12 rounded-8 border border-[#008280] bg-[#d9dbdd] px-12 py-3 dark:border-[#00a9a7] dark:bg-[#0c1723]">
                      <AwesomeIcon
                        name={faCommentsQuestionCheck}
                        className="w-6 h-6 text-[#008280] absolute left-3 top-3 dark:text-[#00a9a7]"
                      />
                      <textarea
                        ref={textareaRef}
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        placeholder={t("support.add_comment_follow_up")}
                        rows={1}
                        className="w-full bg-transparent font-sans text-base font-normal leading-6 tracking-[0] text-[#0c1723] placeholder:text-[#9ea3a8] border-0 outline-none resize-none p-0 block dark:text-[#f6f7f7] dark:placeholder:text-[#474f58]"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleAddComment();
                          }
                        }}
                        onInput={(e) => {
                          const target = e.target as HTMLTextAreaElement;
                          target.style.height = "auto";
                          target.style.height = `${target.scrollHeight}px`;
                          if (sendBtnRef.current) {
                            const isMultiLine = target.scrollHeight > 24;
                            sendBtnRef.current.style.top = isMultiLine ? "auto" : "12px";
                            sendBtnRef.current.style.bottom = isMultiLine
                              ? "12px"
                              : "auto";
                          }
                        }}
                      />
                      <button
                        ref={sendBtnRef}
                        type="button"
                        onClick={handleAddComment}
                        disabled={!commentText.trim() || addCommentMutation.isLoading}
                        className="flex h-6 w-6 items-center justify-center text-[#008280] hover:opacity-90 disabled:opacity-40 transition-opacity absolute right-3 top-3 dark:text-[#00a9a7]"
                        aria-label={t("support.add_comment_follow_up", {
                          defaultValue: "Add comment to follow up",
                        })}
                      >
                        {addCommentMutation.isLoading ? (
                          <Loader size="small" containerClassName="!py-0 !gap-0" />
                        ) : (
                          <AwesomeIcon name={faPaperPlaneTop} className="w-6 h-6" />
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  /* Settings page: original layout (Figma updates apply only to modal / variant "dark") */
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-700 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0 flex-1 text-left">
          <span className="text-base font-semibold text-gray-800 dark:text-gray-200 shrink-0">
            {ticketKey}
          </span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex items-center gap-2">
            {isLoading ? (
              <Loader size="small" />
            ) : (
              <>
                <span
                  className={classNames(
                    "w-3 h-3 rounded-full",
                    getStatusColor(statusColor)
                  )}
                />
                <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  {statusDisplay}
                </span>
              </>
            )}
          </div>
          <AwesomeIcon
            name={isExpanded ? faChevronUp : faChevronDown}
            className="w-4 h-4 text-gray-600 dark:text-gray-400"
          />
        </div>
      </button>

      {isExpanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-6 space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-red-800 dark:text-red-200">
                {t("support.failed_to_load_ticket_info")}: {error.message}
              </p>
            </div>
          )}

          {isLoading && (
            <div className="flex justify-center items-center py-8">
              <Loader size="medium" label={t("support.loading_ticket_info")} />
            </div>
          )}

          {ticket && !isLoading && (
            <div className="space-y-4">
              <h5 className="text-base font-bold text-gray-800 dark:text-gray-200">
                {ticket?.fields?.summary ?? ""}
              </h5>

              {ticket?.fields?.description && (
                <div
                  className="text-base font-medium text-gray-800 dark:text-gray-200 prose prose-sm dark:prose-invert max-w-none [&_*]:text-base [&_*]:font-medium"
                  dangerouslySetInnerHTML={{
                    __html: formatNewlinesAsBr(
                      jiraRichTextToPlainString(ticket.fields.description)
                    ),
                  }}
                />
              )}

              <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center justify-center gap-3 min-w-0">
                  {ticket?.fields?.assignee ? (
                    <>
                      {ticket.fields.assignee?.avatarUrls?.["48x48"] && (
                        <img
                          src={ticket.fields.assignee.avatarUrls["48x48"]}
                          alt={
                            ticket.fields.assignee?.displayName ?? t("support.assignee")
                          }
                          className="w-8 h-8 rounded-full shrink-0"
                        />
                      )}
                      <div className="min-w-0">
                        <p className="text-gray-600 dark:text-gray-400">
                          {t("support.assigned_to")}
                        </p>
                        <p className="font-medium text-gray-800 dark:text-gray-200 truncate">
                          {ticket.fields.assignee?.displayName ??
                            t("support.not_available")}
                        </p>
                      </div>
                    </>
                  ) : (
                    <span className="text-gray-500 dark:text-gray-500">
                      {t("support.not_available")}
                    </span>
                  )}
                </div>
                <div className="text-center">
                  <p>
                    {t("support.created")}:{" "}
                    {ticket?.fields?.created
                      ? new Date(ticket.fields.created).toLocaleDateString()
                      : t("support.not_available")}
                  </p>
                  <p>
                    {t("support.last_updated")}:{" "}
                    {ticket?.fields?.updated
                      ? new Date(ticket.fields.updated).toLocaleDateString()
                      : t("support.not_available")}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                {t("support.comments")}
              </h4>
              {commentCount > 0 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowComments(!showComments);
                  }}
                  className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 text-sm font-medium"
                >
                  {showComments
                    ? t("support.hide_comments")
                    : t("support.view_comments_count", {
                        count: commentCount,
                        defaultValue:
                          commentCount === 1
                            ? "View 1 comment"
                            : `View ${commentCount} comments`,
                      })}
                </button>
              )}
            </div>

            {(commentCount > 0 ? showComments : true) && (
              <div className="space-y-4">
                {isLoadingComments && (
                  <div className="flex justify-center items-center py-4">
                    <Loader size="small" label={t("support.loading_comments")} />
                  </div>
                )}

                {comments.length > 0 ? (
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {comments.map((comment) => (
                      <div
                        key={comment.id}
                        className="border-b border-gray-200 dark:border-gray-700 pb-4 last:border-b-0"
                      >
                        <div className="flex items-start gap-3">
                          {comment.author?.avatarUrls?.["48x48"] && (
                            <img
                              src={comment.author.avatarUrls["48x48"]}
                              alt={
                                comment.author?.displayName ?? t("support.comment_author")
                              }
                              className="w-8 h-8 rounded-full"
                            />
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                {comment.author?.displayName ?? t("support.unknown")}
                              </p>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {comment.created
                                  ? new Date(comment.created).toLocaleString()
                                  : t("support.not_available")}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">
                              {jiraRichTextToPlainString(comment.body)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  !isLoadingComments && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                      {t("support.no_comments_yet")}
                    </p>
                  )
                )}

                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <h5 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
                    {t("support.add_comment_heading")}
                  </h5>
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder={t("support.add_comment_follow_up")}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:focus:border-transparent"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleAddComment();
                      }
                    }}
                  />
                  <div className="flex justify-end mt-2">
                    <Button
                      isPrimary
                      onClick={handleAddComment}
                      disabled={!commentText.trim()}
                      isLoading={addCommentMutation.isLoading}
                      className="sm:px-6"
                    >
                      {addCommentMutation.isLoading
                        ? t("support.adding")
                        : t("support.add_comment")}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketListItem;
